/**
 * VideoWalkHistory — the project-detail Video Walk history panel.
 *
 * Lists Video Walks newest-first with an inline expandable Summary /
 * Segments / Transcript view. Also surfaces legacy Voice Notes so they
 * remain accessible under a single Job Walk media header — as PO required.
 */
import { useEffect, useState } from "react";
import {
  Video, Mic, Sparkles, ChevronDown, ChevronUp, PlusCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import VideoWalkRecorder from "./VideoWalkRecorder";
import VideoWalkSummary from "./VideoWalkSummary";
import LeviReviewConversation from "./LeviReviewConversation";
import CaptureCoachingBanner from "./CaptureCoachingBanner";

export default function VideoWalkHistory({ projectId, initialOpen = false, autoStart = false }) {
  const [walks, setWalks] = useState([]);
  const [legacyVoice, setLegacyVoice] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [recording, setRecording] = useState(!!initialOpen);

  // Vision Conversational Deployment · Checkpoint 1 (PO 2026-08-05).
  // When Levi activates Vision via voice/text, the parent passes
  // ``initialOpen`` so this section mounts the recorder in
  // "prepared" state immediately (D-2: no auto-record, contractor
  // still taps Start Walkthrough).
  useEffect(() => {
    if (initialOpen) setRecording(true);
  }, [initialOpen]);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${projectId}/media`);
      setWalks(r.data.video_walks || []);
      setLegacyVoice(r.data.legacy_voice_notes || []);
    } catch {
      setWalks([]);
      setLegacyVoice([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) refresh();
  }, [projectId]);

  const onFinalized = async () => {
    await refresh();
    setRecording(false);
  };

  return (
    <section className="jc-card p-6" data-testid="video-walk-history">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="overline">Job Walk media</div>
          <p className="text-xs text-[var(--jc-text-soft)] mt-1 max-w-lg">
            Record the job. Describe what you see. Levi organizes the scope.
          </p>
        </div>
        {!recording && (
          <button
            type="button"
            onClick={() => setRecording(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 text-white px-3 py-1.5 text-sm font-semibold hover:bg-red-700"
            data-testid="video-walk-history-record-btn"
          >
            <PlusCircle size={14} /> Record a Video Job Walk
          </button>
        )}
      </div>

      {recording && (
        <div className="mb-6">
          <VideoWalkRecorder projectId={projectId} onFinalized={onFinalized} embedded autoStart={autoStart} />
        </div>
      )}

      {loading && (
        <div className="text-sm text-[var(--jc-text-soft)]">Loading Job Walk media…</div>
      )}

      {!loading && walks.length === 0 && legacyVoice.length === 0 && !recording && (
        <div className="rounded-md border border-dashed border-[var(--jc-border)] p-6 text-center">
          <div className="text-sm font-medium">No Video Job Walks yet</div>
          <p className="text-xs text-[var(--jc-text-soft)] mt-1">
            Start a Video Job Walk to capture the job with narrated video, contractor-stated
            measurements, and a Levi-organized scope.
          </p>
        </div>
      )}

      {/* Video Job Walks (primary) */}
      {walks.length > 0 && (
        <div className="space-y-3">
          <div className="text-xs uppercase tracking-wide text-[var(--jc-text-soft)]">Video Job Walk History</div>
          {walks.map((w) => (
            <div key={w.id} className="border border-[var(--jc-border)] rounded-lg" data-testid={`video-walk-history-item-${w.id}`}>
              <button
                type="button"
                className="w-full flex items-center gap-3 p-3 text-left"
                onClick={() => setOpenId(openId === w.id ? null : w.id)}
              >
                <div className="rounded-full bg-blue-100 p-2">
                  <Sparkles size={12} className="text-blue-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {w.video_walk_summary?.headline || (w.status === "finalized" ? "Finalized Video Job Walk" : "Video Job Walk (in progress)")}
                  </div>
                  <div className="text-xs text-[var(--jc-text-soft)]">
                    {Math.round(w.total_duration_seconds)}s ·
                    {" "}{Math.round((w.total_bytes || 0) / (1024 * 1024) * 10) / 10} MB ·
                    {" "}{new Date(w.created_at).toLocaleString()}
                  </div>
                </div>
                {openId === w.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {openId === w.id && (
                <div className="border-t border-[var(--jc-border)] p-3">
                  <VideoWalkSummary session={w} />
                  {/* Slice VI-01 · Levi-led review conversation is
                      the lead post-walk experience. Renders below
                      the deterministic session summary and reads
                      from GET /video-walks/{sid}/vision-review. */}
                  {w.status === "finalized" && (
                    <div className="mt-4">
                      <LeviReviewConversation session={w} />
                    </div>
                  )}
                  {/* Slice VI-01 · Bounded checkpoint coaching
                      banner. Only surfaces active prompts while the
                      session is still collecting. */}
                  {w.status !== "finalized" && (
                    <div className="mt-4">
                      <CaptureCoachingBanner sessionId={w.id} />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Legacy voice notes (deprecated as primary capture) */}
      {legacyVoice.length > 0 && (
        <div className="mt-6 space-y-2">
          <div className="text-xs uppercase tracking-wide text-[var(--jc-text-soft)]">
            Legacy voice notes
          </div>
          <p className="text-xs text-[var(--jc-text-soft)]">
            Existing voice notes are retained. New captures should use Record a Video Job Walk.
          </p>
          <ul className="space-y-2" data-testid="legacy-voice-notes">
            {legacyVoice.map((v) => (
              <li key={v.id} className="border border-[var(--jc-border)] rounded p-3 text-sm">
                <div className="flex items-center gap-2 text-xs text-[var(--jc-text-soft)]">
                  <Mic size={12} /> {v.original_filename} · {new Date(v.created_at).toLocaleString()} · {v.status}
                </div>
                {v.transcript && (
                  <p className="mt-2 whitespace-pre-wrap text-[var(--jc-text-soft)]">
                    {v.transcript}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
